import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { H4, Text } from "@amzn/stencil-react-components/text";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CameraIcon from "../../../assets/images/cameraIcon.png";

type FailureModeTypes = "NO_DEVICE" | "NO_PERMISSION" | "OTHER";
export type MediaCheckWrapperProps = {
  children?: React.ReactNode | React.ReactNodeArray;
};
type FailureDisplayProps = {
  failureMode: FailureModeTypes;
};

export const MediaCheckWrapper = ({
  children,
}: MediaCheckWrapperProps): JSX.Element => {
  const [failureMode, setFailureMode] = useState<
    FailureModeTypes | undefined
  >();
  const [finishedMediaCheck, setFinishedMediaCheck] = useState<boolean>(false);
  const intl = useIntl();

  const setFailureModeFromMediaCapabilities = (): void => {
    // Get all media streams. Will cause the permission dialog to be shown. If there is no error, then we will close
    // the media streams and the user can move on. If they reject the permissions, sets the showModal state to show the error modal.
    // TODO: Can we differentiate between no camera and no permissions?
    void navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((mediaStreams) => {
        // Media stream resources were initialized and permissions granted/device is present.

        // Close media streams. Should turn off the camera light and allow it to be used again.
        mediaStreams.getTracks().forEach((track) => {
          track.stop && track.stop();
        });
        setFailureMode(undefined);
        return;
      })
      .catch((err: DOMException) => {
        // Media stream resource was not successfully acquired. Check the error message for the reason and
        // set a failure message.
        // @see https://developer.mozilla.org/en-US/docs/Web/API/DOMException
        switch (err.name) {
          case "NotFoundError":
            setFailureMode("NO_DEVICE");
            break;
          case "NotAllowedError":
            setFailureMode("NO_PERMISSION");
            break;
          default:
            setFailureMode("OTHER");
            break;
        }
      })
      .finally(() => {
        // Render children when media check is finished.
        setFinishedMediaCheck(true);
      });
  };

  function FailureDisplay({ failureMode }: FailureDisplayProps): JSX.Element {
    switch (failureMode) {
      case "NO_DEVICE":
        return (
          <Text dataTestId="no-device-error-text">
            <FormattedMessage id="mediacheck-failure-nodevice" />
          </Text>
        );
      case "NO_PERMISSION":
        return (
          <Text dataTestId="no-permission-error-text">
            <FormattedMessage
              id="mediacheck-failure-permission"
              values={{
                icon: (
                  <img
                    height="20px"
                    width="20px"
                    src={CameraIcon}
                    alt={intl.formatMessage({
                      id: "mediacheck-failure-cameraalt",
                    })}
                  />
                ),
              }}
            />
          </Text>
        );
      case "OTHER":
        return (
          <Text dataTestId="other-error-text">
            <FormattedMessage id="mediacheck-failure-other" />
          </Text>
        );
    }
  }

  useEffect(() => {
    setFailureModeFromMediaCapabilities();
  }, []);

  return (
    <>
      {failureMode && (
        <View
          alignItems="center"
          backgroundColor="color.surface.dark-bg-static"
          display="flex"
          height="431px"
          justifyContent="center"
          padding="2rem"
        >
          <View
            justifyContent="center"
            width="800px"
            height="300px"
            backgroundColor="color.surface.bg-default"
            padding="32px"
          >
            <Col gridGap="S300">
              <H4 fontWeight="regular">
                <FormattedMessage id="mediacheck-failure-title" />
              </H4>

              <FailureDisplay failureMode={failureMode} />

              {/*Push the button to the bottom of the container by way of spacing*/}
              <Spacer height="S700" />
              <Row justifyContent="right">
                <Button
                  onClick={setFailureModeFromMediaCapabilities}
                  variant={ButtonVariant.Primary}
                >
                  <FormattedMessage id="identityCheck-button-retry" />
                </Button>
              </Row>
            </Col>
          </View>
        </View>
      )}
      {finishedMediaCheck && !failureMode && children}
    </>
  );
};
